import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Home, FileText, Download, Mail } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [booking, setBooking] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Fetch booking details
          if (bookingId) {
            const bookingRes = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
            setBooking(bookingRes.data);
            // Check if contract was generated (indicates email was sent)
            if (bookingRes.data.contract_pdf_path) {
              setEmailSent(true);
            }
          }
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (pollCount < 5) {
          // Continue polling
          setPollCount(prev => prev + 1);
        } else {
          // Max polls reached, assume pending
          setStatus('success');
          if (bookingId) {
            const bookingRes = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
            setBooking(bookingRes.data);
            if (bookingRes.data.contract_pdf_path) {
              setEmailSent(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (pollCount >= 3) {
          setStatus('failed');
        }
      }
    };

    if (status === 'checking') {
      const timeout = setTimeout(checkPaymentStatus, 2000);
      return () => clearTimeout(timeout);
    }
  }, [sessionId, bookingId, pollCount, status]);

  const handleDownloadContract = async () => {
    if (!bookingId) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/bookings/${bookingId}/contract`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Geneva_Rental_Contract_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!bookingId) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/bookings/${bookingId}/invoice`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Geneva_Rental_Invoice_${booking?.invoice_number || bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  return (
    <Layout>
      <div className="section-container py-20">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'checking' && (
            <div
              
              
              className="space-y-6"
            >
              <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
              <h1 className="font-serif text-3xl text-[#181B1F]">Processing your payment...</h1>
              <p className="text-gray-500">Please wait while we confirm your booking</p>
            </div>
          )}

          {status === 'success' && (
            <div
              
              
              className="space-y-8"
            >
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              
              <div className="space-y-2">
                <h1 className="font-serif text-4xl text-[#181B1F]">{t('booking.success')}</h1>
                <p className="text-gray-500 text-lg">{t('booking.successMessage')}</p>
              </div>

              {booking && (
                <div className="glass-surface p-6 text-left space-y-4" data-testid="booking-confirmation">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-400">{t('booking.bookingId')}</span>
                    <span className="text-[#181B1F] font-mono text-lg">{booking.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Vehicle</span>
                      <span className="text-[#181B1F]">{booking.vehicle_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pickup</span>
                      <span className="text-[#181B1F]">
                        {booking.pickup_date} at {booking.pickup_time || '10:00'} • {booking.pickup_location_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Return</span>
                      <span className="text-[#181B1F]">
                        {booking.return_date} at {booking.return_time || '10:00'} • {booking.return_location_name}
                      </span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-gray-200">
                      <span className="text-gray-400">{t('booking.total')}</span>
                      <span className="text-[#181B1F] text-lg font-medium">
                        CHF {booking.total_price?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('booking.deposit')}</span>
                      <span className="text-yellow-400">
                        CHF {booking.deposit_amount?.toFixed(2)} (pre-authorized)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Info: Documents will be sent with delivery report */}
              <div className="glass-surface p-6 space-y-4" data-testid="documents-section">
                <h3 className="text-[#181B1F] font-medium flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {t('booking.documentsInfo', 'About Your Documents')}
                </h3>
                
                <p className="text-gray-600 text-sm">
                  {t('booking.documentsDeliveryNote', 'Your rental contract and invoice will be sent to you by email when you pick up the vehicle. You will receive the complete signed documents after the delivery report is completed.')}
                </p>
                
                {/* Only show invoice download - contract sent with delivery report */}
                <Button
                  onClick={handleDownloadInvoice}
                  className="btn-outline flex items-center justify-center gap-2 w-full sm:w-auto"
                  data-testid="download-invoice-btn"
                >
                  <Download className="w-4 h-4" />
                  {t('booking.downloadInvoice', 'Download Invoice')}
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  onClick={() => navigate('/')}
                  className="btn-primary"
                  data-testid="back-home-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('booking.backToHome')}
                </Button>
              </div>

              {emailSent && (
                <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>Confirmation email sent to {booking?.customer_info?.email}</span>
                </div>
              )}
              
              {!emailSent && booking?.customer_info?.email && (
                <p className="text-gray-400 text-sm">
                  A confirmation email will be sent to {booking?.customer_info?.email}
                </p>
              )}
            </div>
          )}

          {status === 'failed' && (
            <div
              
              
              className="space-y-8"
            >
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              
              <div className="space-y-2">
                <h1 className="font-serif text-4xl text-[#181B1F]">Payment Failed</h1>
                <p className="text-gray-500 text-lg">
                  Unfortunately, we couldn't process your payment. Please try again.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  onClick={() => navigate('/vehicles')}
                  className="btn-outline"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="btn-primary"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('booking.backToHome')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BookingSuccessPage;
